import { createNoise2D } from 'simplex-noise';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  life: number;
  maxLife: number;
  color: string;
}

export class ParticleSystem {
  particles: Particle[] = [];
  private maxParticles = 90;
  private ambientParticles: Particle[] = [];
  private noise2D = createNoise2D();
  private t = 0;

  constructor(private canvasWidth: number, private canvasHeight: number) {
    this.initAmbient();
  }

  private initAmbient(): void {
    this.ambientParticles = [];
    for (let i = 0; i < 50; i++) {
      this.ambientParticles.push({
        x: Math.random() * this.canvasWidth,
        y: Math.random() * this.canvasHeight,
        vx: 0,
        vy: 0,
        size: Math.random() * 2.2 + 0.6,
        alpha: Math.random() * 0.2 + 0.05,
        life: Infinity,
        maxLife: Infinity,
        color: `hsla(${Math.random() * 60 + 190}, 70%, 75%, 1)`,
      });
    }
  }

  resize(w: number, h: number): void {
    this.canvasWidth = w;
    this.canvasHeight = h;
    this.initAmbient();
  }

  burst(x: number, y: number, color: string, count: number = 12): void {
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
      const speed = Math.random() * 2.5 + 1;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: Math.random() * 3 + 1,
        alpha: 0.85,
        life: 60,
        maxLife: 60,
        color,
      });
    }
    if (this.particles.length > this.maxParticles) {
      this.particles = this.particles.slice(-this.maxParticles);
    }
  }

  update(amplitude = 0): void {
    this.t += 0.004;
    // Ambient with Simplex Noise Flow Field
    for (const p of this.ambientParticles) {
      const angle = this.noise2D(p.x * 0.0015, p.y * 0.0015 + this.t) * Math.PI * 2;
      const speed = 0.3 + amplitude * 1.8;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed - 0.15;
      p.x += p.vx;
      p.y += p.vy;

      if (p.y < -10) { p.y = this.canvasHeight + 10; p.x = Math.random() * this.canvasWidth; }
      if (p.y > this.canvasHeight + 10) { p.y = -10; p.x = Math.random() * this.canvasWidth; }
      if (p.x < -10) { p.x = this.canvasWidth + 10; p.y = Math.random() * this.canvasHeight; }
      if (p.x > this.canvasWidth + 10) { p.x = -10; p.y = Math.random() * this.canvasHeight; }
    }

    // Burst
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.98;
      p.vy *= 0.98;
      p.life--;
      p.alpha = (p.life / p.maxLife) * 0.85;
      if (p.life <= 0) this.particles.splice(i, 1);
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    // Ambient
    for (const p of this.ambientParticles) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(180, 200, 255, ${p.alpha})`;
      ctx.fill();
    }

    // Burst
    for (const p of this.particles) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      const c = p.color;
      const match = c.match(/hsla?\((\d+),\s*(\d+)%,\s*(\d+)%/);
      if (match) {
        ctx.fillStyle = `hsla(${match[1]}, ${match[2]}%, ${match[3]}%, ${p.alpha})`;
      } else {
        ctx.fillStyle = `rgba(255, 255, 255, ${p.alpha})`;
      }
      ctx.fill();
    }
  }

  pulseAmplitude(amplitude: number): void {
    const intensity = Math.min(1, amplitude * 8);
    for (const p of this.ambientParticles) {
      p.alpha = (Math.random() * 0.15 + 0.03) + intensity * 0.15;
    }
  }
}
